// Pure helpers for per-line Discipline-Mismatch acknowledgement at staffing-request
// commit time. Every mismatched Primary line carries its OWN justification (audit
// fidelity: one `discipline_mismatch_acknowledged` row per line, never a blanket
// reason shared across mismatches). Kept React-/DB-free so they're unit-testable.

/** Minimum length of an acknowledgement reason (matches assignResource's rule). */
export const MIN_MISMATCH_REASON_LENGTH = 10;

/** The only shape these helpers need from a CommitMismatch — its line id. */
interface HasLineId {
  lineId: string;
}

/**
 * True when every mismatched line has a reason of at least the minimum length
 * (after trimming). An empty `mismatches` list is trivially complete.
 */
export function mismatchReasonsComplete(
  mismatches: readonly HasLineId[],
  reasons: Readonly<Record<string, string>>,
): boolean {
  return mismatches.every(
    (m) => (reasons[m.lineId]?.trim().length ?? 0) >= MIN_MISMATCH_REASON_LENGTH,
  );
}

/**
 * Build the per-line acknowledgement map to send with the commit: keyed by line id,
 * trimmed, and restricted to the mismatched lines (reasons for non-mismatch lines
 * are dropped). Pair with `mismatchReasonsComplete` to gate submission.
 */
export function buildMismatchAcknowledgementReasons(
  mismatches: readonly HasLineId[],
  reasons: Readonly<Record<string, string>>,
): Record<string, string> {
  return Object.fromEntries(mismatches.map((m) => [m.lineId, (reasons[m.lineId] ?? "").trim()]));
}
